"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SpecSheetsColorChartsSamples() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "Phenolic Laminate",
    description: "Industrial-grade, UV-stable phenolic sheets designed for harsh environments and permanent identification.",
    tensile: "85 MPa",
    temp: "130°C",
    Icon: Layers
  }, {
    name: "Anodized Aluminum",
    description: "Precision-engraved aluminum offering superior corrosion resistance and high-contrast legibility.",
    tensile: "150 MPa",
    temp: "200°C",
    Icon: Droplet
  }, {
    name: "Stainless Steel",
    description: "316-grade stainless steel for extreme chemical exposure and high-heat industrial applications.",
    tensile: "520 MPa",
    temp: "800°C",
    Icon: Beaker
  }, {
    name: "Traffolyte",
    description: "Multi-layered engraving plastic ideal for electrical switchgear, control panels, and safety tags.",
    tensile: "40 MPa",
    temp: "90°C",
    Icon: Box
  }, {
    name: "Acrylic",
    description: "High-clarity, impact-resistant material perfect for architectural signage and custom control overlays.",
    tensile: "70 MPa",
    temp: "85°C",
    Icon: Atom
  }, {
    name: "Brass",
    description: "Premium architectural brass for high-end equipment identification and aesthetic industrial branding.",
    tensile: "350 MPa",
    temp: "400°C",
    Icon: Shield
  }, {
    name: "Polycarbonate",
    description: "Durable, heat-resistant film for membrane switches and high-performance industrial labels.",
    tensile: "65 MPa",
    temp: "120°C",
    Icon: Cpu
  }, {
    name: "Ceramic Coated",
    description: "Advanced ceramic-infused surfaces providing unmatched durability against abrasion and chemical wear.",
    tensile: "200 MPa",
    temp: "1000°C",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="spec-sheets-color-charts-samples" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--chart-1)"
          }}>
              Technical Resources
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Materials engineered for industrial excellence.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              Explore our comprehensive range of industrial materials. From high-heat metals to 
              durable laminates, we provide the technical specifications and samples required 
              for your most demanding engineering projects.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md hover:shadow-lg" style={{
            backgroundColor: "var(--card)"
          }}>
                {}
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300" style={{
              backgroundColor: "var(--muted)"
            }}>
                  <Icon className="h-6 w-6 transition-colors duration-300 group-hover:text-background" strokeWidth={1.25} style={{
                color: "var(--chart-1)"
              }} />
                  <span className="absolute pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-[60px] w-[60px] rounded-lg -z-0" aria-hidden="true" />
                </div>

                {}
                <style>{`
                  .group:hover [data-badge-fill="${i}"] {
                    background-color: var(--chart-1);
                  }
                `}</style>

                {}
                <h3 className="font-serif font-default text-[28px] leading-tight text-card-foreground mb-3">
                  {material.name}
                </h3>

                {}
                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {material.description}
                </p>

                {}
                <div className="pt-5 border-t border-border">
                  <p className="font-mono text-xs tracking-wider uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--chart-1)"
                }}>STRENGTH:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--chart-1)"
                }}>LIMIT:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>

      {}
      <style>{`
        #spec-sheets-color-charts-samples .group:hover .material-badge-icon {
          color: var(--primary-foreground);
        }
      `}</style>
    </section>;
}